import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { CarrierOperator, CarrierOperatorsResponse } from "@/api/carrier";
import { useSocket } from "./socket-context";

/**
 * Map of server socket events → react-query keys to invalidate.
 * Server is already room-scoped per carrier, so any event we receive
 * is relevant to this dispatcher.
 */
const DISPATCH_MUTATION_QUERY_KEYS: string[][] = [
  ["carrier", "trips"],
  ["carrier", "trip"],
  ["carrier", "tasks"],
  ["carrier", "operators"],
];
const LIVE_DISPATCH_QUERY_KEYS: string[][] = [
  ...DISPATCH_MUTATION_QUERY_KEYS,
  ["carrier", "dispatch", "time-windows"],
];

const EVENT_INVALIDATIONS: Record<string, string[][]> = {
  // Prefix invalidation refreshes the selected Trip and its waypoint detail.
  "trip:updated": DISPATCH_MUTATION_QUERY_KEYS,
  "trip:assigned": DISPATCH_MUTATION_QUERY_KEYS,
  "trip:deleted": DISPATCH_MUTATION_QUERY_KEYS,
  "task:updated": DISPATCH_MUTATION_QUERY_KEYS,
  "task:deleted": [
    ["carrier", "tasks"],
    ["carrier", "trips"],
    ["carrier", "trip"],
  ],
  "task:created": [
    ["carrier", "tasks"],
    ["carrier", "trips"],
  ],
  "waypoint:updated": [
    ["carrier", "trips"],
    ["carrier", "trip"],
    ["carrier", "tasks"],
  ],
  "operator:updated": DISPATCH_MUTATION_QUERY_KEYS,
  "operator:deleted": DISPATCH_MUTATION_QUERY_KEYS,
  "shipment:updated": [["carrier", "shipments"]],
  "pickup-booking:updated": [["carrier", "pickup-bookings"]],
};

type OperatorHeartbeat = {
  operatorId: string;
  location?: { lat?: number; lng?: number } | null;
  status?: string;
  isTracking?: boolean;
  timestamp: string | number;
};

function heartbeatTime(payload: OperatorHeartbeat): number {
  const parsed =
    typeof payload.timestamp === "number"
      ? payload.timestamp
      : Date.parse(payload.timestamp);
  return Number.isFinite(parsed) ? parsed : 0;
}

function patchOperator(
  operator: CarrierOperator,
  payload: OperatorHeartbeat
): CarrierOperator {
  if (operator.id !== payload.operatorId) return operator;
  const incomingTime = heartbeatTime(payload);
  const currentTime = operator.lastLocationTime
    ? Date.parse(operator.lastLocationTime)
    : 0;
  if (incomingTime < currentTime) return operator;
  const mapLocation =
    typeof payload.location?.lat === "number" &&
    typeof payload.location.lng === "number"
      ? { lat: payload.location.lat, lng: payload.location.lng }
      : undefined;
  return {
    ...operator,
    ...(payload.status && { status: payload.status }),
    ...(typeof payload.isTracking === "boolean" && {
      isTracking: payload.isTracking,
    }),
    ...(incomingTime > 0 && {
      lastLocationTime: new Date(incomingTime).toISOString(),
    }),
    ...(payload.location && { lastLocation: payload.location }),
    ...(mapLocation && { mapLocation }),
  };
}

function patchOperatorCache(
  current: CarrierOperatorsResponse | CarrierOperator | undefined,
  payload: OperatorHeartbeat
) {
  if (!current) return current;
  if ("data" in current && Array.isArray(current.data)) {
    return {
      ...current,
      data: current.data.map((operator) => patchOperator(operator, payload)),
    };
  }
  return patchOperator(current as CarrierOperator, payload);
}

/**
 * Mount once at the app root. Wires server-emitted carrier-scoped events
 * to react-query cache invalidations so screens auto-refresh.
 */
export function useCarrierSocketSubscriptions() {
  const { socket } = useSocket();
  const qc = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const handlers: Array<[string, () => void]> = [];
    const latestHeartbeat = new Map<string, number>();
    let disposed = false;
    const pendingInvalidations = new Map<string, string[]>();
    let invalidationScheduled = false;
    const scheduleInvalidations = (queryKeys: string[][]) => {
      for (const queryKey of queryKeys) {
        pendingInvalidations.set(JSON.stringify(queryKey), queryKey);
      }
      if (invalidationScheduled) return;
      invalidationScheduled = true;
      queueMicrotask(() => {
        invalidationScheduled = false;
        if (disposed) return;
        const queued = Array.from(pendingInvalidations.values());
        pendingInvalidations.clear();
        for (const queryKey of queued) {
          void qc.invalidateQueries({ queryKey });
        }
      });
    };

    for (const [event, queryKeys] of Object.entries(EVENT_INVALIDATIONS)) {
      const handler = () => {
        scheduleInvalidations(queryKeys);
      };
      socket.on(event, handler);
      handlers.push([event, handler]);
    }

    const onHeartbeat = (payload: OperatorHeartbeat) => {
      const timestamp = heartbeatTime(payload);
      if (timestamp < (latestHeartbeat.get(payload.operatorId) ?? 0)) return;
      latestHeartbeat.set(payload.operatorId, timestamp);
      void qc.cancelQueries({ queryKey: ["carrier", "operators"] }).then(() => {
        if (
          disposed ||
          timestamp < (latestHeartbeat.get(payload.operatorId) ?? 0)
        ) {
          return;
        }
        qc.setQueriesData<CarrierOperatorsResponse | CarrierOperator>(
          { queryKey: ["carrier", "operators"] },
          (current) => patchOperatorCache(current, payload)
        );
      });
    };
    const onConnect = () => {
      scheduleInvalidations(LIVE_DISPATCH_QUERY_KEYS);
    };
    socket.on("operator:heartbeat", onHeartbeat);
    socket.on("connect", onConnect);

    return () => {
      disposed = true;
      for (const [event, handler] of handlers) {
        socket.off(event, handler);
      }
      socket.off("operator:heartbeat", onHeartbeat);
      socket.off("connect", onConnect);
    };
  }, [socket, qc]);
}
